import { useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  ArrowLeftRight,
  Sparkles,
  AlertTriangle,
  UsersRound,
  User,
  Info,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
  Tabs,
  type TabItem,
} from '../../ui';
import { EntityRefLinks } from '../common/EntityRefLinks';
import { OverlapPanel } from './OverlapPanel';
import { useExperts } from '../../features/intelligence/useExperts';
import type { ExpertHit } from '../../features/intelligence/types';

type ExpertsTab = 'experts' | 'overlap';

const EXAMPLE_TOPICS = ['authentication', 'checkout', 'data model', 'observability'];

/** One expert starting-point card: who + why (roles) + supporting entities. */
function ExpertCard({ hit }: { hit: ExpertHit }) {
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-surface-border bg-surface-3 text-accent">
            {hit.isTeam ? (
              <UsersRound className="h-4 w-4" />
            ) : (
              <User className="h-4 w-4" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-zinc-100">{hit.who}</p>
            <p className="text-xs text-zinc-500">{hit.isTeam ? 'Team' : 'Individual'}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {hit.roles.map((role) => (
            <Badge key={role} tone="info">
              {role}
            </Badge>
          ))}
        </div>

        <EntityRefLinks
          refs={hit.refs}
          heading={`Where (${hit.refs.length})`}
        />
      </CardBody>
    </Card>
  );
}

/**
 * Experts — awareness-only discovery. Given a topic, surface starting points of
 * expertise (from workstreams, completed & in-flight stories, doc authorship,
 * decision participation, and service ownership) with WHY each surfaced. A
 * second tab surfaces possible overlapping work to prompt collaboration. No
 * productivity, velocity, or ranking metrics anywhere — just who to talk to.
 */
export function ExpertsPage() {
  const navigate = useNavigate();
  const {
    result,
    overlaps,
    workstreamCount,
    loading,
    error,
    find,
    clear,
  } = useExperts();

  const [tab, setTab] = useState<ExpertsTab>('experts');
  const [input, setInput] = useState('');

  const tabItems = useMemo<TabItem[]>(
    () => [
      { id: 'experts', label: 'Find experts', icon: <Users className="h-4 w-4" /> },
      {
        id: 'overlap',
        label: `Overlapping work${overlaps.length ? ` (${overlaps.length})` : ''}`,
        icon: <ArrowLeftRight className="h-4 w-4" />,
      },
    ],
    [overlaps.length],
  );

  const submit = () => {
    const q = input.trim();
    if (!q) return;
    find(q);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };
  const onExample = (topic: string) => {
    setInput(topic);
    find(topic);
  };

  return (
    <div>
      <PageHeader
        icon={<Users className="h-5 w-5" />}
        title="Experts & Collaboration"
        description="Find who knows about a topic and spot work that might overlap — for awareness and collaboration, never ranking."
      />

      <div className="mb-5">
        <Tabs items={tabItems} value={tab} onChange={(id) => setTab(id as ExpertsTab)} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner label="Loading team context" />
        </div>
      ) : error ? (
        <EmptyState
          icon={<AlertTriangle className="h-6 w-6" />}
          title="Couldn't load team context"
          description={error}
        />
      ) : tab === 'experts' ? (
        <div className="space-y-5">
          <Card>
            <CardBody className="space-y-3">
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[16rem] flex-1">
                  <Input
                    label="Topic, system, or area"
                    placeholder="e.g. authentication, checkout, data model…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                  />
                </div>
                <Button
                  onClick={submit}
                  disabled={!input.trim()}
                  leftIcon={<Search className="h-4 w-4" />}
                >
                  Find
                </Button>
                {result && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setInput('');
                      clear();
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-zinc-500">Try:</span>
                {EXAMPLE_TOPICS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => onExample(t)}
                    className="rounded-full border border-surface-border bg-surface-2 px-2.5 py-0.5 text-xs text-zinc-300 transition-colors hover:border-accent/40 hover:bg-surface-3"
                  >
                    {t}
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>

          <div className="flex items-start gap-2 rounded-xl border border-surface-border bg-surface-1/60 px-4 py-3 text-sm text-zinc-400">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
            <p>
              Results are derived client-side from real work — they list roles and
              where each person shows up, as starting points for a conversation.
              They are ordered alphabetically, never scored or ranked.
            </p>
          </div>

          {!result ? (
            <EmptyState
              icon={<Sparkles className="h-6 w-6" />}
              title="Search for a topic"
              description="Enter a system, feature, or area to find people who have worked on it and where."
            />
          ) : result.experts.length === 0 ? (
            <EmptyState
              icon={<Search className="h-6 w-6" />}
              title={`No experts found for "${result.topic}"`}
              description="Nothing recorded matches that topic yet. Try a broader term or a system name."
            />
          ) : (
            <div>
              <p className="mb-3 text-sm text-zinc-400">
                {result.experts.length}{' '}
                {result.experts.length === 1 ? 'starting point' : 'starting points'} for{' '}
                <span className="text-zinc-200">"{result.topic}"</span> · from{' '}
                {result.matchedRefs.length} matching{' '}
                {result.matchedRefs.length === 1 ? 'entity' : 'entities'}
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {result.experts.map((hit) => (
                  <ExpertCard key={`${hit.isTeam ? 'team' : 'user'}:${hit.who}`} hit={hit} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <OverlapPanel
          overlaps={overlaps}
          workstreamCount={workstreamCount}
          onOpenWorkstream={(id) => navigate(`/workstreams/${encodeURIComponent(id)}`)}
        />
      )}
    </div>
  );
}

export default ExpertsPage;
